import React, { useEffect, useState } from "react";
import {
  Modal,
  Table,
  Button,
  message,
  Tooltip,
  Space,
  Tag,
  Typography,
  Popconfirm,
  Alert,
  Empty,
} from "antd";
import {
  ReloadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  WarningOutlined,
  CloseCircleFilled,
  DesktopOutlined,
  WifiOutlined,
  DisconnectOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "../../css/modal-list-sync.css"; // Importamos los estilos CSS

const { Text } = Typography;

// Interfaz para los elementos en cola
interface QueuedItem {
  id: string;
  url: string;
  timestamp: number;
  status: string;
  attempts: number;
  method: string;
  data?: any;
}

export const SyncQueueManager: React.FC<{
  visible: boolean;
  onClose: () => void;
  onSync?: () => void;
}> = ({ visible, onClose, onSync }) => {
  const [queuedItems, setQueuedItems] = useState<QueuedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedItem, setSelectedItem] = useState<QueuedItem | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Manejar cambios en el estado de conexión
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Cargar elementos de la cola al abrir el modal
  useEffect(() => {
    if (visible) {
      loadQueuedItems();
    }
  }, [visible]);

  const loadQueuedItems = async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const items = await getQueuedItems();
      setQueuedItems(items);
    } catch (error) {
      console.error("Error al cargar elementos en cola:", error);
      setErrorMessage(
        "No se pudieron cargar los elementos en cola. " +
          (error instanceof Error ? error.message : "")
      );
      // En caso de error, establecer un array vacío
      setQueuedItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Función para normalizar la estructura de los elementos de la cola
  const normalizeQueueItem = (item: any): QueuedItem => {
    // Intentar extraer datos relevantes de diferentes estructuras posibles
    let url: string | undefined;
    let method: string | undefined;
    let data: any = null;

    // Extraer URL de diferentes posibles ubicaciones
    if (item.url) {
      url = item.url;
    } else if (item.request && item.request.url) {
      url = item.request.url;
    } else if (item.requestData && item.requestData.url) {
      url = item.requestData.url;
    }

    // Extraer método HTTP
    if (item.method) {
      method = item.method;
    } else if (item.request && item.request.method) {
      method = item.request.method;
    } else if (item.requestData && item.requestData.method) {
      method = item.requestData.method;
    }

    // Extraer datos
    if (item.data) {
      data = item.data;
    } else if (item.requestData && item.requestData.body) {
      try {
        // Si es string, intentar parsear como JSON
        if (typeof item.requestData.body === "string") {
          data = JSON.parse(item.requestData.body);
        } else {
          data = item.requestData.body;
        }
      } catch (e) {
        data = item.requestData.body;
      }
    } else if (item.request && item.request.body) {
      data = item.request.body;
    }

    return {
      id:
        item.id ||
        item.requestId ||
        `id-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      url: url || "URL no disponible",
      timestamp: item.timestamp || item.createdAt || Date.now(),
      status: item.status || "pending",
      attempts: item.attempts || 0,
      method: method || "POST",
      data: data,
    };
  };

  // Función mejorada para obtener elementos de la cola específica wf-cg-container-post-offline
  const getQueuedItems = async (): Promise<QueuedItem[]> => {
    return new Promise((resolve, reject) => {
      // Si no hay indexedDB disponible o estamos en una ventana privada
      if (!window.indexedDB) {
        console.log("IndexedDB no está disponible en este navegador");
        resolve([]);
        return;
      }

      try {
        // Enfocarse específicamente en la base de datos y almacén que mencionaste
        const dbName = "workbox-background-sync";
        const storeName = "wf-cg-container-post-offline";

        console.log(`Intentando abrir específicamente: ${dbName}/${storeName}`);

        // Intentar abrir la base de datos
        const request = indexedDB.open(dbName);

        request.onerror = (event) => {
          console.error(`Error al abrir la base de datos ${dbName}:`, event);
          setErrorMessage(
            `No se pudo acceder a la base de datos de sincronización: ${
              (event.target as IDBOpenDBRequest).error?.message ||
              "Error desconocido"
            }`
          );
          resolve([]);
        };

        request.onsuccess = () => {
          const db = request.result;

          // Verificar si el almacén específico existe
          if (!db.objectStoreNames.contains(storeName)) {
            console.log(
              `El almacén ${storeName} no existe en la base de datos ${dbName}`
            );

            // Si el store no existe, intentar buscar en 'requests' que podría ser un nombre alternativo
            if (db.objectStoreNames.contains("requests")) {
              console.log("Usando 'requests' como almacén alternativo");
              getItemsFromStore(db, "requests", resolve);
            } else {
              db.close();
              setErrorMessage(
                `No se encontró el almacén de datos para las solicitudes pendientes`
              );
              resolve([]);
            }
            return;
          }

          // Si el almacén existe, obtener los elementos
          getItemsFromStore(db, storeName, resolve);
        };
      } catch (error) {
        console.error("Error crítico al intentar acceder a IndexedDB:", error);
        setErrorMessage(
          `Error inesperado al acceder a la base de datos: ${
            error instanceof Error ? error.message : "Error desconocido"
          }`
        );
        resolve([]);
      }
    });
  };

  // Función auxiliar para obtener elementos de un almacén específico
  const getItemsFromStore = (
    db: IDBDatabase,
    storeName: string,
    resolve: (items: QueuedItem[]) => void
  ) => {
    try {
      const transaction = db.transaction([storeName], "readonly");
      const store = transaction.objectStore(storeName);
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        let items = getAllRequest.result || [];
        console.log(`Se encontraron ${items.length} elementos en ${storeName}`);

        // Normalizar los datos para asegurar que tienen la estructura correcta
        items = items.map((item: any) => normalizeQueueItem(item));

        db.close();
        resolve(items);
      };

      getAllRequest.onerror = (event) => {
        console.error(`Error al obtener elementos de ${storeName}:`, event);
        db.close();
        resolve([]);
      };

      transaction.oncomplete = () => {
        // Asegurarse de que la base de datos se cierre apropiadamente
        if (db.version > 0) {
          db.close();
        }
      };

      transaction.onerror = (event) => {
        console.error(`Error en la transacción con ${storeName}:`, event);
        db.close();
        resolve([]);
      };
    } catch (error) {
      console.error(`Error al acceder al almacén ${storeName}:`, error);
      db.close();
      resolve([]);
    }
  };

  // Intentar reenviar un elemento específico
  const handleRetryItem = async (id: string) => {
    if (!navigator.serviceWorker.controller) {
      message.error("Service Worker no está disponible");
      return;
    }

    if (!navigator.onLine) {
      message.warning(
        "No hay conexión a internet. Intenta nuevamente cuando estés online."
      );
      return;
    }

    setLoading(true);
    try {
      // Enviar mensaje al Service Worker para procesar este elemento específico
      navigator.serviceWorker.controller.postMessage({
        type: "RETRY_SPECIFIC_ITEM",
        id,
      });

      message.loading("Reenviando solicitud...");

      // Esperar un momento y luego recargar la lista
      setTimeout(() => {
        loadQueuedItems();
        if (onSync) onSync(); // Notificar al componente padre
      }, 2000);
    } catch (error) {
      console.error("Error al reenviar elemento:", error);
      message.error("Error al reenviar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  // Eliminar un elemento de la cola
  const handleDeleteItem = async (id: string) => {
    setLoading(true);
    try {
      await deleteQueuedItem(id);
      message.success("Elemento eliminado de la cola");
      loadQueuedItems();
      if (onSync) onSync(); // Notificar al componente padre para actualizar contadores
    } catch (error) {
      console.error("Error al eliminar elemento:", error);
      message.error("No se pudo eliminar el elemento");
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar elemento de IndexedDB específicamente de wf-cg-container-post-offline
  const deleteQueuedItem = async (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        // Enfocarse específicamente en la base de datos y almacén que mencionaste
        const dbName = "workbox-background-sync";
        const storeName = "wf-cg-container-post-offline";

        const request = indexedDB.open(dbName);

        request.onerror = (event) => {
          console.error(
            `Error al abrir la base de datos ${dbName} para eliminar:`,
            event
          );
          reject(
            new Error(
              `No se pudo acceder a la base de datos: ${
                (event.target as IDBOpenDBRequest).error?.message ||
                "Error desconocido"
              }`
            )
          );
        };

        request.onsuccess = () => {
          const db = request.result;

          // Verificar si el almacén específico existe
          if (!db.objectStoreNames.contains(storeName)) {
            // Si el store no existe, intentar con 'requests'
            if (db.objectStoreNames.contains("requests")) {
              deleteItemFromStore(db, "requests", id, resolve, reject);
            } else {
              db.close();
              reject(
                new Error(`No se encontró el almacén de datos ${storeName}`)
              );
            }
            return;
          }

          // Si el almacén existe, eliminar el elemento
          deleteItemFromStore(db, storeName, id, resolve, reject);
        };
      } catch (error) {
        console.error(
          "Error crítico al intentar eliminar de IndexedDB:",
          error
        );
        reject(
          new Error(
            `Error inesperado al eliminar: ${
              error instanceof Error ? error.message : "Error desconocido"
            }`
          )
        );
      }
    });
  };

  // Función auxiliar para eliminar un elemento de un almacén específico
  const deleteItemFromStore = (
    db: IDBDatabase,
    storeName: string,
    id: string,
    resolve: () => void,
    reject: (error: Error) => void
  ) => {
    try {
      const transaction = db.transaction([storeName], "readwrite");
      const store = transaction.objectStore(storeName);
      const deleteRequest = store.delete(id);

      deleteRequest.onsuccess = () => {
        console.log(`Elemento eliminado correctamente de ${storeName}`);
        db.close();
        resolve();
      };

      deleteRequest.onerror = (event) => {
        console.error(`Error al eliminar elemento de ${storeName}:`, event);
        db.close();
        reject(
          new Error(
            `Error al eliminar: ${
              (event.target as IDBRequest).error?.message || "Error desconocido"
            }`
          )
        );
      };

      transaction.oncomplete = () => {
        // Asegurarse de que la base de datos se cierre apropiadamente
        if (db.version > 0) {
          db.close();
        }
      };

      transaction.onerror = (event) => {
        console.error(
          `Error en la transacción de eliminación con ${storeName}:`,
          event
        );
        db.close();
        reject(
          new Error(
            `Error en la transacción: ${
              (event.target as IDBTransaction).error?.message ||
              "Error desconocido"
            }`
          )
        );
      };
    } catch (error) {
      console.error(
        `Error al acceder al almacén ${storeName} para eliminar:`,
        error
      );
      db.close();
      reject(
        new Error(
          `Error inesperado al eliminar: ${
            error instanceof Error ? error.message : "Error desconocido"
          }`
        )
      );
    }
  };

  // Mostrar detalles de un elemento
  const showItemDetails = (item: QueuedItem) => {
    setSelectedItem(item);
    setDetailsVisible(true);
  };

  // Opciones de la tabla
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (text: string | undefined) => {
        if (!text) return <Text>ID no disponible</Text>;
        return (
          <Text ellipsis style={{ maxWidth: 120 }} title={text}>
            {text.substring(0, 8)}...
          </Text>
        );
      },
      responsive: ["md"],
    },
    {
      title: "Endpoint",
      dataIndex: "url",
      key: "url",
      render: (url: string | undefined) => {
        // Verificar si url está definido
        if (!url) {
          return <Text>URL no disponible</Text>;
        }

        try {
          // Procesar la URL para extraer información específica para solicitudes de wf-cg-container-post-offline
          if (
            url.includes("/workflow-container") ||
            url.includes("/api/workflow")
          ) {
            // Extraer la parte final de la URL (el tipo de flujo de trabajo)
            const parts = url.split("/").filter((part) => part.trim() !== "");
            const endpoint =
              parts.length > 0 ? parts[parts.length - 1] : "desconocido";

            // Mapear endpoints específicos a nombres más descriptivos
            const endpointMap: Record<string, string> = {
              "workflow-container-checkpoint": "Checkpoint",
              "workflow-container-empty": "Container Vacío",
              "workflow-container-videos": "Videos",
              "workflow-container-full": "Container Completo",
              "workflow-truck": "Camión",
              // Añadir más mapeos según sea necesario
            };

            // Determinar el color según el tipo de flujo
            const colorMap: Record<string, string> = {
              checkpoint: "blue",
              empty: "orange",
              videos: "purple",
              full: "green",
              truck: "cyan",
              // Añadir más mapeos según sea necesario
            };

            let displayName = endpoint;
            let color = "default";

            // Verificar si tenemos un nombre más descriptivo
            for (const [key, value] of Object.entries(endpointMap)) {
              if (endpoint.includes(key)) {
                displayName = value;
                // Determinar color basado en el tipo
                for (const [typeKey, typeColor] of Object.entries(colorMap)) {
                  if (key.includes(typeKey)) {
                    color = typeColor;
                    break;
                  }
                }
                break;
              }
            }

            return (
              <Tooltip title={url}>
                <Tag color={color}>{displayName}</Tag>
              </Tooltip>
            );
          }

          // Para otras URLs, mostrar simplemente el endpoint
          const parts = url.split("/").filter((part) => part.trim() !== "");
          const endpoint =
            parts.length > 0 ? parts[parts.length - 1] : "desconocido";

          return (
            <Tooltip title={url}>
              <Text>{endpoint}</Text>
            </Tooltip>
          );
        } catch (error) {
          console.error("Error al procesar URL:", error);
          return <Text>Error en URL</Text>;
        }
      },
    },
    {
      title: "Fecha",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp: number | undefined) => {
        if (!timestamp) return <Text>Fecha desconocida</Text>;
        return (
          <Tooltip title={dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss")}>
            <Text>{dayjs(timestamp).format("DD/MM/YYYY HH:mm")}</Text>
          </Tooltip>
        );
      },
      responsive: ["sm"],
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status: string | undefined, record: QueuedItem) => {
        let color = "blue";
        let text = "Pendiente";
        let icon = null;

        if (!status) {
          return <Tag color="default">Desconocido</Tag>;
        }

        switch (status) {
          case "pending":
            color = "blue";
            text = "Pendiente";
            icon = <ClockCircleOutlined />;
            break;
          case "in_progress":
            color = "orange";
            text = "En proceso";
            icon = <SyncOutlined spin />;
            break;
          case "failed":
            color = "red";
            text = `Fallido (${record.attempts || 0})`;
            icon = <WarningOutlined />;
            break;
          case "completed":
            color = "green";
            text = "Completado";
            icon = <InfoCircleOutlined />;
            break;
          default:
            color = "default";
            text = status || "Desconocido";
        }

        return (
          <Tag color={color} icon={icon} className="status-tag">
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Método",
      dataIndex: "method",
      key: "method",
      render: (method: string | undefined) => {
        if (!method) return <Tag color="default">Desconocido</Tag>;
        return (
          <Tag color="purple" className="method-tag">
            {method}
          </Tag>
        );
      },
      responsive: ["lg"],
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: any, record: QueuedItem) => (
        <Space size="small" className="action-buttons">
          <Tooltip title="Ver detalles">
            <Button
              icon={<InfoCircleOutlined />}
              size="small"
              onClick={() => showItemDetails(record)}
              className="action-button details-button"
            />
          </Tooltip>
          <Tooltip
            title={!isOnline ? "Necesitas conexión a internet" : "Reintentar"}
          >
            <Button
              icon={<ReloadOutlined />}
              size="small"
              type="primary"
              disabled={!isOnline}
              onClick={() => handleRetryItem(record.id)}
              className="action-button retry-button"
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Popconfirm
              title="¿Eliminar solicitud?"
              description="Esta solicitud se eliminará permanentemente"
              onConfirm={() => handleDeleteItem(record.id)}
              okText="Sí"
              cancelText="No"
            >
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                className="action-button delete-button"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Para seleccionar múltiples filas
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  // Eliminar elementos seleccionados
  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;

    setLoading(true);
    try {
      // Eliminar cada elemento seleccionado
      for (const key of selectedRowKeys) {
        await deleteQueuedItem(key.toString());
      }

      message.success(`${selectedRowKeys.length} elementos eliminados`);
      setSelectedRowKeys([]);
      loadQueuedItems();
      if (onSync) onSync(); // Notificar al componente padre
    } catch (error) {
      console.error("Error al eliminar elementos:", error);
      message.error("Error al eliminar algunos elementos");
    } finally {
      setLoading(false);
    }
  };

  // Procesar todos los elementos
  const handleProcessAll = () => {
    if (!navigator.serviceWorker.controller) {
      message.error("Service Worker no está disponible");
      return;
    }

    if (!isOnline) {
      message.warning(
        "No hay conexión a internet. La sincronización se realizará automáticamente cuando vuelvas a estar online."
      );
      return;
    }

    // Enviar mensaje al Service Worker para procesar toda la cola
    navigator.serviceWorker.controller.postMessage({
      type: "SYNC_NOW",
    });

    message.loading("Procesando solicitudes pendientes...");

    // Dar tiempo para que el SW comience a procesar
    setTimeout(() => {
      loadQueuedItems();
      if (onSync) onSync();
    }, 2000);
  };

  return (
    <>
      <Modal
        title={
          <div className="queue-modal-title">
            <ClockCircleOutlined className="queue-modal-icon" />
            <span>Gestor de Cola de Sincronización</span>
            {queuedItems.length > 0 && (
              <Tag color="blue" className="queue-count-tag">
                {queuedItems.length} elemento(s)
              </Tag>
            )}
            <Tag
              color={isOnline ? "green" : "red"}
              icon={isOnline ? <WifiOutlined /> : <DisconnectOutlined />}
              className="connection-status-tag"
            >
              {isOnline ? "Online" : "Offline"}
            </Tag>
          </div>
        }
        open={visible}
        onCancel={onClose}
        width="90vw"
        className="sync-queue-modal"
        footer={[
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              key="refresh"
              onClick={loadQueuedItems}
              icon={<ReloadOutlined />}
              className="refresh-button"
            >
              <span className="button-text">Refrescar</span>
            </Button>

            <Button
              key="process-all"
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleProcessAll}
              disabled={!isOnline || queuedItems.length === 0}
              className="process-button"
            >
              <span className="button-text">Procesar Todo</span>
            </Button>

            <Button
              key="delete-selected"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDeleteSelected}
              disabled={selectedRowKeys.length === 0}
              className="delete-selected-button"
            >
              <span className="button-text">Eliminar</span>{" "}
              <span className="selection-count">
                ({selectedRowKeys.length})
              </span>
            </Button>

            <Button
              icon={<CloseCircleFilled />}
              key="close"
              onClick={onClose}
              className="close-button close-button-gray"
            >
              Cerrar
            </Button>
          </div>,
        ]}
      >
        {!isOnline && (
          <Alert
            message="Modo offline activo"
            description="No hay conexión a internet. Las operaciones de sincronización estarán limitadas hasta que recuperes la conexión."
            type="warning"
            showIcon
            icon={<DisconnectOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        {errorMessage && (
          <Alert
            message="Error al cargar datos"
            description={errorMessage}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMessage(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          rowSelection={rowSelection}
          columns={columns as any}
          dataSource={
            Array.isArray(queuedItems)
              ? queuedItems.map((item) => ({
                  ...item,
                  key:
                    item.id ||
                    `key-${Math.random().toString(36).substring(2, 9)}`,
                }))
              : []
          }
          loading={loading}
          pagination={{ pageSize: 10 }}
          size="small"
          className="sync-queue-table"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    {loading
                      ? "Cargando elementos..."
                      : errorMessage
                      ? "Error al cargar elementos"
                      : "No hay elementos en la cola de sincronización"}
                  </span>
                }
              />
            ),
          }}
          scroll={{ x: "max-content" }}
        />
      </Modal>

      {/* Modal de detalles del elemento */}
      <Modal
        title={
          <div className="details-modal-title">
            <InfoCircleOutlined className="details-modal-icon" />
            <span>Detalles de la Solicitud</span>
            {selectedItem?.method && (
              <Tag color="purple" style={{ marginLeft: 8 }}>
                {selectedItem.method}
              </Tag>
            )}
          </div>
        }
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        className="details-modal"
        footer={[
          <Button
            key="close"
            onClick={() => setDetailsVisible(false)}
            className="close-button"
          >
            Cerrar
          </Button>,
        ]}
        width={700}
      >
        {selectedItem && (
          <div className="details-content">
            {/* Sección de información básica */}
            <div className="details-section">
              <h3 className="details-section-title">Información General</h3>
              <div className="details-grid">
                <div className="details-item">
                  <strong>ID:</strong>
                  <span className="details-value">
                    {selectedItem.id || "Sin ID"}
                  </span>
                </div>
                <div className="details-item">
                  <strong>Estado:</strong>
                  <span className="details-value">
                    <Tag
                      color={
                        selectedItem.status === "pending"
                          ? "blue"
                          : selectedItem.status === "in_progress"
                          ? "orange"
                          : selectedItem.status === "failed"
                          ? "red"
                          : selectedItem.status === "completed"
                          ? "green"
                          : "default"
                      }
                    >
                      {selectedItem.status === "pending"
                        ? "Pendiente"
                        : selectedItem.status === "in_progress"
                        ? "En Progreso"
                        : selectedItem.status === "failed"
                        ? "Fallido"
                        : selectedItem.status === "completed"
                        ? "Completado"
                        : selectedItem.status || "Desconocido"}
                    </Tag>
                  </span>
                </div>
                <div className="details-item">
                  <strong>Fecha:</strong>
                  <span className="details-value">
                    {selectedItem.timestamp
                      ? dayjs(selectedItem.timestamp).format(
                          "DD/MM/YYYY HH:mm:ss"
                        )
                      : "Fecha desconocida"}
                  </span>
                </div>
                <div className="details-item">
                  <strong>Intentos:</strong>
                  <span className="details-value">
                    {selectedItem.attempts !== undefined &&
                    selectedItem.attempts !== null
                      ? selectedItem.attempts
                      : 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Sección de URL */}
            <div className="details-section">
              <h3 className="details-section-title">Información de Endpoint</h3>
              <div className="details-item">
                <strong>URL Completa:</strong>
                <span className="details-value url-value">
                  {selectedItem.url || "URL desconocida"}
                </span>
              </div>

              {selectedItem.url &&
                selectedItem.url.includes("workflow-container") && (
                  <div className="details-item">
                    <strong>Tipo de Flujo:</strong>
                    <span className="details-value">
                      {selectedItem.url.includes("checkpoint") ? (
                        <Tag color="blue">Checkpoint</Tag>
                      ) : selectedItem.url.includes("empty") ? (
                        <Tag color="orange">Container Vacío</Tag>
                      ) : selectedItem.url.includes("videos") ? (
                        <Tag color="purple">Videos</Tag>
                      ) : selectedItem.url.includes("full") ? (
                        <Tag color="green">Container Completo</Tag>
                      ) : selectedItem.url.includes("truck") ? (
                        <Tag color="cyan">Camión</Tag>
                      ) : (
                        <Tag>Flujo Desconocido</Tag>
                      )}
                    </span>
                  </div>
                )}
            </div>

            {/* Sección de datos */}
            {selectedItem.data && (
              <div className="details-section">
                <h3 className="details-section-title">Datos de la Solicitud</h3>

                {/* Si los datos son un objeto con estructura específica de workflow, mostrar info relevante */}
                {typeof selectedItem.data === "object" &&
                  selectedItem.data !== null && (
                    <>
                      {/* Intentar extraer información relevante de flujos de trabajo */}
                      {(selectedItem.data.containerId ||
                        selectedItem.data.truckId) && (
                        <div className="details-grid">
                          {selectedItem.data.containerId && (
                            <div className="details-item">
                              <strong>ID de Contenedor:</strong>
                              <span className="details-value">
                                {selectedItem.data.containerId}
                              </span>
                            </div>
                          )}
                          {selectedItem.data.truckId && (
                            <div className="details-item">
                              <strong>ID de Camión:</strong>
                              <span className="details-value">
                                {selectedItem.data.truckId}
                              </span>
                            </div>
                          )}
                          {selectedItem.data.checkpointId && (
                            <div className="details-item">
                              <strong>ID de Checkpoint:</strong>
                              <span className="details-value">
                                {selectedItem.data.checkpointId}
                              </span>
                            </div>
                          )}
                          {selectedItem.data.clientId && (
                            <div className="details-item">
                              <strong>ID de Cliente:</strong>
                              <span className="details-value">
                                {selectedItem.data.clientId}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                {/* Siempre mostrar los datos completos en formato JSON */}
                <div className="details-item">
                  <strong>Datos Completos:</strong>
                </div>
                <pre className="details-data">
                  {typeof selectedItem.data === "object"
                    ? JSON.stringify(selectedItem.data, null, 2)
                    : String(selectedItem.data)}
                </pre>
              </div>
            )}

            {/* Sección de acciones */}
            <div className="details-section details-actions">
              <Space>
                <Tooltip
                  title={
                    !isOnline ? "Necesitas conexión a internet" : "Reintentar"
                  }
                >
                  <Button
                    icon={<ReloadOutlined />}
                    type="primary"
                    disabled={!isOnline}
                    onClick={() => {
                      handleRetryItem(selectedItem.id);
                      setDetailsVisible(false);
                    }}
                  >
                    Reintentar Envío
                  </Button>
                </Tooltip>
                <Popconfirm
                  title="¿Eliminar solicitud?"
                  description="Esta solicitud se eliminará permanentemente"
                  onConfirm={() => {
                    handleDeleteItem(selectedItem.id);
                    setDetailsVisible(false);
                  }}
                  okText="Sí"
                  cancelText="No"
                >
                  <Button icon={<DeleteOutlined />} danger>
                    Eliminar
                  </Button>
                </Popconfirm>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

// Componente de botón para mostrar el gestor de cola
export const SyncQueueButton: React.FC<{
  pendingCount?: number;
}> = ({ pendingCount = 0 }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Actualizar estado de conexión
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <>
      <Button
        type="primary"
        icon={<ClockCircleOutlined />}
        onClick={() => setModalVisible(true)}
        className="sync-queue-button"
      >
        <span className="button-text">Cola de Sincronización</span>
        {pendingCount > 0 && (
          <Tag color="red" className="pending-count-tag">
            {pendingCount}
          </Tag>
        )}
        {!isOnline && (
          <Tooltip title="Sin conexión a internet">
            <DisconnectOutlined style={{ marginLeft: 5, color: "#ff4d4f" }} />
          </Tooltip>
        )}
      </Button>

      {modalVisible && (
        <ErrorBoundary
          fallback={
            <Modal
              title="Error al cargar el gestor de cola"
              open={true}
              onCancel={() => setModalVisible(false)}
              footer={[
                <Button key="close" onClick={() => setModalVisible(false)}>
                  Cerrar
                </Button>,
              ]}
            >
              <Alert
                message="Error inesperado"
                description="Ocurrió un error al intentar cargar el gestor de cola. Esto puede deberse a problemas con la base de datos local o la conexión."
                type="error"
                showIcon
              />
              <div style={{ marginTop: 16 }}>
                <p>Sugerencias:</p>
                <ul>
                  <li>Verifica tu conexión a internet</li>
                  <li>Actualiza la página</li>
                  <li>Limpia la caché del navegador si el problema persiste</li>
                </ul>
              </div>
            </Modal>
          }
        >
          <SyncQueueManager
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
          />
        </ErrorBoundary>
      )}
    </>
  );
};

// Componente ErrorBoundary para capturar errores
class ErrorBoundary extends React.Component<{
  children: React.ReactNode;
  fallback: React.ReactNode;
}> {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Error en componente:", error);
    console.error("Información del error:", errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}
